// Repositorio propio de `enterprise` (plan.md §3.3). Envuelve `@shared/api/http` directo — NO
// `clientCompanies.api.js` legado (ese sigue vivo como puente para `compute`/vms y `clients`,
// decisión L).
//
// Migrado a v1 (T52). Decisiones de contrato (specs/api/core-manage-api-v1.json):
// - La familia CRUD (`GET/POST /clients/{clientId}/companies`, `GET/PATCH/DELETE
//   /clients/{clientId}/companies/{companyId}`) anida la empresa bajo su cliente — **C-7**:
//   `clientId` se propaga como primer parámetro en TODOS los métodos del repositorio (incluidos
//   el que sigue sin equivalente v1, ver más abajo), cascada hasta los 3 hooks consumidores
//   (`useClientCompanies`, `useClientCompanyForm`, `useCompanyDatacenters`) — mismo criterio que
//   `vmCluster.repository.ts` (T38) y `clientDatacenter.repository.ts` (T46). `update` usa PATCH
//   (ya no PUT).
// - `ClientCompanySummaryDto` (list) / `ClientCompanyDetailDto` (get/create/update) llegan
//   camelCase, ApiResponse desenvuelto — se convierten con `toSnakeCaseKeys`/`toItemArray` de
//   `@shared/api/keys` antes de `.parse()`. `vmCount`/`deviceCount`/`datacenterLinkCount` (tras la
//   conversión: `vm_count`/`device_count`/`datacenter_link_count`) se renombran en `toDomain` a los
//   nombres que ya consume `presentation/` (`vms_count`/`device_instances_count`/
//   `datacenters_count`, sin tocar ese borde — decisión L).
// - `description` solo existe en `ClientCompanyDetailDto` (get/create/update), ausente en
//   `ClientCompanySummaryDto` (list) — el schema lo trata `nullish` en ambos casos, sin exigirlo.
// - `id` es UUID string en v1 (a diferencia del legado con ids numéricos): se simplifica el schema
//   a `z.string()`, sin coerción (memoria repo `api-v1-id-coercion-gotcha`).
// - `addDatacenter` migra a `POST /clients/{clientId}/companies/{companyId}/datacenter-links`
//   (`CompanyDatacenterLinkDto`, sin schema de request documentado — `JsonObject`, se envía tal
//   cual arma la UI, mismo criterio que el resto de writes de este archivo).
// - **Huecos v1** (no se resuelven aquí, mismo criterio que el resto de bloqueantes G-1..G-9):
//   `getDatacenters` (listar asociaciones existentes) no tiene GET equivalente en v1 — el único
//   endpoint de `datacenter-links` documentado es el POST de arriba (`CompanyDatacenterLinkDto` se
//   referencia una sola vez en todo el spec, descartado como huérfano); se conserva el legado
//   `GET /client-companies/:id/datacenters` sin tocar. El DELETE sin equivalente v1 se retiró tras
//   quedar el unlink no accionable y confirmar cero consumidores productivos (T33).

import { z } from 'zod';
import apiClient from '@shared/api/http';
import { toSnakeCaseKeys, toItemArray } from '@shared/api/keys';
import type { Id } from '../domain/enterprise.types';
import type { ClientCompany, CompanyDatacenterAssociation } from '../domain/clientCompany.types';

const ClientCompanyDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  nit: z.string().nullish(),
  description: z.string().nullish(),
  status: z
    .string()
    .nullish()
    .transform((s) => (s === 'inactive' ? 'inactive' : 'active')) // default 'active' (spec.md §2)
    .pipe(z.enum(['active', 'inactive'])),
  vm_count: z.number().nullish(),
  device_count: z.number().nullish(),
  datacenter_link_count: z.number().nullish(),
});

function toDomain(dto: z.infer<typeof ClientCompanyDtoSchema>): ClientCompany {
  return {
    id: dto.id as Id,
    name: dto.name,
    nit: dto.nit,
    description: dto.description,
    status: dto.status,
    vms_count: dto.vm_count,
    device_instances_count: dto.device_count,
    datacenters_count: dto.datacenter_link_count,
  };
}

// Sub-recurso de asociaciones — sigue en el legado (ver comentario de cabecera, huecos v1), sin
// cambios de contrato.
const CompanyDatacenterAssociationDtoSchema = z.object({
  association_id: z.union([z.string(), z.number()]).transform(String),
  name: z.string(),
  kind: z.enum(['kyndryl', 'client']),
  city: z.string().nullish(),
  country_name: z.string().nullish(),
  notes: z.string().nullish(),
});

export const clientCompanyRepository = {
  async getByClient(clientId: Id): Promise<ClientCompany[]> {
    const { data } = await apiClient.get(`/clients/${clientId}/companies`);
    return z
      .array(ClientCompanyDtoSchema)
      .parse(toSnakeCaseKeys(toItemArray(data)))
      .map(toDomain);
  },

  async create(clientId: Id, payload: Record<string, unknown>): Promise<ClientCompany> {
    const { data } = await apiClient.post(`/clients/${clientId}/companies`, payload);
    return toDomain(ClientCompanyDtoSchema.parse(toSnakeCaseKeys(data)));
  },

  // v1 anidó la empresa bajo su cliente y actualiza con PATCH (spec.md C-7 / mapping.md §S-3).
  async update(clientId: Id, id: Id, payload: Record<string, unknown>): Promise<ClientCompany> {
    const { data } = await apiClient.patch(`/clients/${clientId}/companies/${id}`, payload);
    return toDomain(ClientCompanyDtoSchema.parse(toSnakeCaseKeys(data)));
  },

  // Deja pasar el error del backend sin precheck (p. ej. VMs/servidores que quedan sin empresa,
  // spec.md UC-10) — el mensaje de aviso lo muestra presentation/ antes de confirmar.
  async remove(clientId: Id, id: Id): Promise<void> {
    await apiClient.delete(`/clients/${clientId}/companies/${id}`);
  },

  // Hueco v1: sin GET para listar datacenter-links (ver comentario de cabecera) — se conserva el
  // legado. `clientId` se recibe igual por consistencia de firma (C-7), sin usarse en la URL.
  async getDatacenters(_clientId: Id, companyId: Id): Promise<CompanyDatacenterAssociation[]> {
    const { data } = await apiClient.get(`/client-companies/${companyId}/datacenters`);
    return z.array(CompanyDatacenterAssociationDtoSchema).parse(data);
  },

  async addDatacenter(
    clientId: Id,
    companyId: Id,
    payload: Record<string, unknown>
  ): Promise<void> {
    await apiClient.post(`/clients/${clientId}/companies/${companyId}/datacenter-links`, payload);
  },

  // La lectura individual por id NO se porta: sin consumidor en este contexto (decisión L).
};
